import { disableWorkerAccount } from "../../services/worker/workerAccessLifecycle";
import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { WORKER_TRANSITIONS } from "./WorkflowStates";

function requireUid(workerOrUid) {
  const uid =
    typeof workerOrUid === "string"
      ? workerOrUid
      : workerOrUid?.uid || workerOrUid?.id;

  if (!uid) {
    throw new Error("Worker UID is required.");
  }

  return uid;
}

function actorLabel() {
  return (
    auth.currentUser?.email ||
    auth.currentUser?.uid ||
    "system"
  );
}

async function logTransition({
  workerUid,
  transition,
  details = {},
}) {
  await addDoc(collection(db, "workflowTimeline"), {
    entityType: "worker",
    entityId: workerUid,
    workerUid,
    transition,
    details,
    performedBy: actorLabel(),
    createdAt: serverTimestamp(),
  });
}

async function applyWorkerTransition({
  workerOrUid,
  transition,
  updates,
  details = {},
}) {
  const workerUid = requireUid(workerOrUid);

  const payload = {
    ...updates,
    lastWorkflowTransition: transition,
    lastWorkflowTransitionAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: actorLabel(),
  };
  if ([WORKER_TRANSITIONS.WORKER_SUSPENDED, WORKER_TRANSITIONS.WORKER_ARCHIVED].includes(transition)) {
    await disableWorkerAccount(workerUid, payload);
  } else {
    await updateDoc(doc(db, "users", workerUid), payload);
  }

try {
  await logTransition({
    workerUid,
    transition,
    details,
  });
} catch (logError) {
  console.warn("Workflow timeline logging failed:", logError);
}

  return workerUid;
}

export async function approveRegistration(workerOrUid) {
  return applyWorkerTransition({
    workerOrUid,
    transition: WORKER_TRANSITIONS.REGISTRATION_APPROVED,
    updates: {
      registrationStatus: "approved",
      registrationApproved: true,
      canAccessOnboarding: true,

      onboardingStatus: "registered",
      workerStatus: "onboarding",
      status: "onboarding",
      workerOperationalState: "ONBOARDING",

      registrationApprovedAt: serverTimestamp(),
      registrationApprovedBy: actorLabel(),
    },
  });
}

export async function completeProfile(workerOrUid, profileUpdates = {}) {
  return applyWorkerTransition({
    workerOrUid,
    transition: WORKER_TRANSITIONS.PROFILE_COMPLETED,
    updates: {
      ...profileUpdates,

      profileCompleted: true,
      onboardingStatus: "in_progress",
      workerStatus: "onboarding",
      status: "onboarding",
      workerOperationalState: "ONBOARDING",

      profileCompletedAt: serverTimestamp(),
    },
  });
}

export async function markDocumentsUploading(workerOrUid) {
  return applyWorkerTransition({
    workerOrUid,
    transition: WORKER_TRANSITIONS.DOCUMENTS_UPLOADING,
    updates: {
      onboardingCompleted: false,
      onboardingStatus: "in_progress",
      complianceStatus: "collecting_documents",
      workerStatus: "onboarding",
      status: "onboarding",
      workerOperationalState: "ONBOARDING",
    },
  });
}

export async function submitDocuments(
  workerOrUid,
  credentialMap = {}
) {
  return applyWorkerTransition({
    workerOrUid,
    transition: WORKER_TRANSITIONS.DOCUMENTS_SUBMITTED,
    updates: {
      credentialMap,

      onboardingCompleted: false,
      onboardingStatus: "submitted",
      complianceStatus: "pending_review",
      workerStatus: "under_review",
      status: "under_review",
      workerOperationalState: "UNDER_REVIEW",

      submittedAt: serverTimestamp(),
    },
  });
}

export async function requestReupload(
  workerOrUid,
  {
    credentialMap = {},
    documentKey = "",
    reason = "",
  } = {}
) {
  return applyWorkerTransition({
    workerOrUid,
    transition: WORKER_TRANSITIONS.REUPLOAD_REQUESTED,
    updates: {
      credentialMap,

      onboardingCompleted: false,
      onboardingStatus: "action_required",
      complianceStatus: "reupload_required",
      workerStatus: "action_required",
      status: "action_required",
      workerOperationalState: "ACTION_REQUIRED",

      reuploadDocumentKey: documentKey,
      reuploadReason: reason,
      reuploadRequestedAt: serverTimestamp(),
      reuploadRequestedBy: actorLabel(),
    },
    details: {
      documentKey,
      reason,
    },
  });
}

export async function approveDocuments(
  workerOrUid,
  credentialMap = {}
) {
  return applyWorkerTransition({
    workerOrUid,
    transition: WORKER_TRANSITIONS.DOCUMENTS_APPROVED,
    updates: {
      credentialMap,

      onboardingStatus: "ready_for_approval",
      complianceStatus: "docs_approved",
      workerStatus: "under_review",
      status: "under_review",
      workerOperationalState: "UNDER_REVIEW",

      documentsApprovedAt: serverTimestamp(),
      documentsApprovedBy: actorLabel(),
    },
  });
}

export async function approveWorker(workerOrUid) {
  return applyWorkerTransition({
    workerOrUid,
    transition: WORKER_TRANSITIONS.WORKER_APPROVED,
    updates: {
      registrationStatus: "approved",
      registrationApproved: true,
      canAccessOnboarding: true,

      onboardingStatus: "approved",
      onboardingCompleted: true,

      complianceStatus: "ready",
      workerStatus: "ready",
      status: "active",
      workerOperationalState: "READY",
      canAccessDashboard: true,

      approvedAt: serverTimestamp(),
      approvedBy: actorLabel(),
    },
  });
}

export async function suspendWorker(
  workerOrUid,
  reason = ""
) {
  return applyWorkerTransition({
    workerOrUid,
    transition: WORKER_TRANSITIONS.WORKER_SUSPENDED,
    updates: {
      workerStatus: "suspended",
      status: "suspended",
      workerOperationalState: "SUSPENDED",
      suspensionReason: reason,
      suspendedAt: serverTimestamp(),
      suspendedBy: actorLabel(),
    },
    details: {
      reason,
    },
  });
}

export async function archiveWorker(
  workerOrUid,
  reason = ""
) {
  return applyWorkerTransition({
    workerOrUid,
    transition: WORKER_TRANSITIONS.WORKER_ARCHIVED,
    updates: {
      archived: true,
      workerStatus: "archived",
      status: "archived",
      workerOperationalState: "ARCHIVED",
      archiveReason: reason,
      archivedAt: serverTimestamp(),
      archivedBy: actorLabel(),
    },
    details: {
      reason,
    },
  });
}
